(() => {
  const selectors = {
    reveal: '[data-focus-reveal]',
    product: '[data-focus-product]',
    focusSelect: '[data-focus-select]',
    addressForm: '[data-address-form]',
    addressDelete: '[data-address-delete]',
    collectionSearch: '[data-collection-search]',
    policyContainer: '.shopify-policy__container',
    policyData: '[data-focus-policy-data]',
  };
  const reducedMotionQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const mobileCollectionFiltersQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 749px)') : null;

  const getMotionPreference = () => document.documentElement.dataset.motionPreference || 'system';

  const getMotionMode = () => {
    const preference = getMotionPreference();

    // Accessibility wins: an OS "reduce" request always forces reduced motion,
    // even when the theme setting requests full motion.
    if ((reducedMotionQuery && reducedMotionQuery.matches) || preference === 'reduced') {
      return 'reduced';
    }

    return 'full';
  };

  const applyMotionMode = () => {
    document.documentElement.dataset.motion = getMotionMode();
  };

  const debounce = (callback, wait = 180) => {
    let timeoutId = 0;

    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback(...args), wait);
    };
  };

  const normalizeSearchValue = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const getScopedElements = (scope, selector) => {
    const root = scope instanceof Element ? scope : document;
    const elements = Array.from(root.querySelectorAll(selector));

    if (root instanceof Element && root.matches(selector)) {
      elements.unshift(root);
    }

    return elements;
  };

  const createListboxPopoverController = (button, list) => {
    const supportsPopover = typeof list.showPopover === 'function' && typeof list.hidePopover === 'function';
    const gap = 8;
    const viewportPadding = 12;
    const maxListHeight = 320;
    const minListHeight = 96;
    let isPositioningBound = false;

    if (supportsPopover) {
      list.setAttribute('popover', 'manual');
    }

    const position = () => {
      if (!supportsPopover || list.hidden) {
        return;
      }

      const rect = button.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const maxWidth = Math.max(1, viewportWidth - viewportPadding * 2);
      const width = Math.min(rect.width, maxWidth);
      const left = Math.min(Math.max(rect.left, viewportPadding), viewportWidth - viewportPadding - width);
      const availableBelow = viewportHeight - rect.bottom - gap - viewportPadding;
      const availableAbove = rect.top - gap - viewportPadding;
      const naturalHeight = Math.min(list.scrollHeight || maxListHeight, maxListHeight);
      const openAbove = availableBelow < naturalHeight && availableAbove > availableBelow;
      const availableSpace = Math.max(minListHeight, openAbove ? availableAbove : availableBelow);
      const listHeight = Math.min(naturalHeight, availableSpace, maxListHeight);
      const top = openAbove
        ? Math.max(viewportPadding, rect.top - listHeight - gap)
        : Math.min(rect.bottom + gap, viewportHeight - viewportPadding - listHeight);

      list.style.left = `${Math.round(left)}px`;
      list.style.top = `${Math.round(Math.max(viewportPadding, top))}px`;
      list.style.width = `${Math.round(width)}px`;
      list.style.maxHeight = `${Math.round(listHeight)}px`;
    };

    const bindPositioning = () => {
      if (isPositioningBound) {
        return;
      }

      isPositioningBound = true;
      window.addEventListener('scroll', position, true);
      window.addEventListener('resize', position);
    };

    const unbindPositioning = () => {
      if (!isPositioningBound) {
        return;
      }

      isPositioningBound = false;
      window.removeEventListener('scroll', position, true);
      window.removeEventListener('resize', position);
    };

    return {
      position,
      open() {
        list.hidden = false;

        if (!supportsPopover) {
          return;
        }

        position();

        if (!list.matches(':popover-open')) {
          list.showPopover();
        }

        position();
        bindPositioning();
      },
      close() {
        unbindPositioning();

        if (supportsPopover && list.matches(':popover-open')) {
          list.hidePopover();
        }

        list.hidden = true;
      },
    };
  };

  const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    if (rect.width === 0 || rect.height === 0) {
      return false;
    }

    return rect.bottom >= 0 && rect.right >= 0 && rect.top <= viewportHeight * 0.92 && rect.left <= viewportWidth;
  };

  const setVisible = (elements) => {
    elements.forEach((element) => {
      element.dataset.focusRevealReady = 'true';
      element.classList.add('is-visible');
    });
  };

  const bindFocusReveals = (scope = document) => {
    const targets = getScopedElements(scope, selectors.reveal).filter((element) => !element.dataset.focusRevealReady);

    if (!targets.length) {
      return;
    }

    if (getMotionMode() === 'reduced' || !('IntersectionObserver' in window)) {
      setVisible(targets);
      return;
    }

    // Signal that JS is driving reveals; only now does the CSS hide gated
    // content. Without this marker (no-JS, or this script failing to run),
    // [data-focus-reveal] content stays visible by default.
    document.documentElement.setAttribute('data-reveal-ready', 'true');

    window.setTimeout(() => {
      setVisible(getScopedElements(scope, selectors.reveal).filter((element) => !element.classList.contains('is-visible')));
    }, 1200);

    if (!window.__focusRevealObserver) {
      window.__focusRevealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px 18% 0px',
        }
      );
    }

    targets.forEach((element, index) => {
      element.dataset.focusRevealReady = 'true';

      if (!element.style.getPropertyValue('--focus-reveal-delay')) {
        element.style.setProperty('--focus-reveal-delay', `${Math.min(index * 90, 240)}ms`);
      }

      if (isInViewport(element)) {
        element.classList.add('is-visible');
        return;
      }

      window.__focusRevealObserver.observe(element);
    });
  };

  const bindFocusProducts = (scope = document) => {
    getScopedElements(scope, selectors.product).forEach((productRoot) => {
      if (productRoot.dataset.focusProductReady === 'true') {
        return;
      }

      productRoot.dataset.focusProductReady = 'true';

      const slides = Array.from(productRoot.querySelectorAll('[data-product-media-slide]'));
      const mediaTriggers = Array.from(productRoot.querySelectorAll('[data-product-media-trigger]'));
      const variantSelect = productRoot.querySelector('[data-product-variant-select]');
      const price = productRoot.querySelector('[data-product-price]');
      const compare = productRoot.querySelector('[data-product-compare]');
      const compareValue = productRoot.querySelector('[data-product-compare-value]');
      const save = productRoot.querySelector('[data-product-save]');
      const mediaSave = productRoot.querySelector('[data-product-media-save]');
      const stock = productRoot.querySelector('[data-product-stock]');
      const submit = productRoot.querySelector('[data-product-submit]');
      const submitLabel = productRoot.querySelector('[data-product-submit-label]');
      const buttonPrice = productRoot.querySelector('[data-product-button-price]');
      const sku = productRoot.querySelector('[data-product-sku]');
      const quantityInput = productRoot.querySelector('[data-qty-input]');
      const quantityButtons = productRoot.querySelectorAll('[data-qty-change]');
      const thumbsTrack = productRoot.querySelector('[data-product-thumbs-track]');
      const thumbsPrev = productRoot.querySelector('[data-product-thumbs-prev]');
      const thumbsNext = productRoot.querySelector('[data-product-thumbs-next]');
      const benefitsCard = productRoot.querySelector('[data-pdp-benefits-card]');
      const benefitsDismiss = productRoot.querySelector('[data-pdp-benefits-dismiss]');
      const zoomButton = productRoot.querySelector('[data-product-zoom]');
      const lightbox = productRoot.querySelector('[data-product-lightbox]');
      const lightboxMedia = productRoot.querySelector('[data-product-lightbox-media]');
      const lightboxClose = productRoot.querySelector('[data-product-lightbox-close]');
      const stickyBar = productRoot.querySelector('[data-sticky-atc]');
      const stickyPrice = productRoot.querySelector('[data-sticky-atc-price]');
      const stickyButton = productRoot.querySelector('[data-sticky-atc-button]');

      const getThumbStep = () => {
        if (!thumbsTrack || mediaTriggers.length === 0) {
          return 0;
        }

        const firstTrigger = mediaTriggers[0];
        const trackStyles = window.getComputedStyle(thumbsTrack);
        const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0;

        return firstTrigger.getBoundingClientRect().width + gap;
      };

      const updateThumbNavigation = () => {
        if (!thumbsTrack || !thumbsPrev || !thumbsNext) {
          return;
        }

        const maxScrollLeft = Math.max(thumbsTrack.scrollWidth - thumbsTrack.clientWidth, 0);
        const hasOverflow = maxScrollLeft > 2;

        thumbsPrev.disabled = !hasOverflow || thumbsTrack.scrollLeft <= 2;
        thumbsNext.disabled = !hasOverflow || thumbsTrack.scrollLeft >= maxScrollLeft - 2;
      };

      const getScrollBehavior = () => (getMotionMode() === 'reduced' ? 'auto' : 'smooth');

      const setActiveMedia = (mediaId) => {
        if (!mediaId) {
          return;
        }

        slides.forEach((slide) => {
          slide.classList.toggle('is-active', slide.dataset.productMediaSlide === mediaId);
        });

        mediaTriggers.forEach((trigger) => {
          const isActive = trigger.dataset.productMediaTrigger === mediaId;
          trigger.classList.toggle('is-active', isActive);
          trigger.setAttribute('aria-pressed', isActive ? 'true' : 'false');

          if (isActive && thumbsTrack) {
            trigger.scrollIntoView({ behavior: getScrollBehavior(), block: 'nearest', inline: 'nearest' });
          }
        });

        window.requestAnimationFrame(updateThumbNavigation);
      };

      const getQuantityMax = () => {
        const option = variantSelect ? variantSelect.selectedOptions[0] : null;

        if (option) {
          if (option.dataset.inventoryTracked === 'true' && option.dataset.inventoryPolicy === 'deny') {
            return Math.max(Number(option.dataset.inventoryQuantity || 0), 0);
          }

          return Infinity;
        }

        const staticMax = Number(quantityInput?.dataset.qtyMax);
        return Number.isFinite(staticMax) && staticMax > 0 ? staticMax : Infinity;
      };

      const clampQuantity = () => {
        if (!quantityInput) {
          return;
        }

        const max = getQuantityMax();
        let value = Math.floor(Number(quantityInput.value));

        if (!Number.isFinite(value) || value < 1) {
          value = 1;
        }

        if (Number.isFinite(max) && value > max) {
          value = Math.max(max, 1);
        }

        quantityInput.value = String(value);
      };

      const setQuantityLocked = (locked) => {
        if (quantityInput) {
          quantityInput.disabled = locked;
        }

        quantityButtons.forEach((button) => {
          button.disabled = locked;
        });
      };

      const updatePricing = (option) => {
        if (!option || !price) {
          return;
        }

        const rawPrice = Number(option.dataset.rawPrice || 0);
        const rawCompare = Number(option.dataset.rawCompareAt || 0);
        const isAvailable = option.dataset.available === 'true';
        const hasSavings = rawCompare > rawPrice;

        price.textContent = option.dataset.price || '';

        if (buttonPrice) {
          buttonPrice.textContent = option.dataset.price || '';
        }

        if (compare) {
          if (compareValue) {
            compareValue.textContent = hasSavings ? option.dataset.compareAt || '' : '';
          } else {
            compare.textContent = hasSavings ? option.dataset.compareAt || '' : '';
          }

          compare.classList.toggle('is-hidden', !hasSavings);
        }

        if (save) {
          save.textContent = hasSavings ? `-${Math.round(((rawCompare - rawPrice) / rawCompare) * 100)}%` : '';
          save.classList.toggle('is-hidden', !hasSavings);
        }

        if (mediaSave) {
          mediaSave.textContent = hasSavings ? `-${Math.round(((rawCompare - rawPrice) / rawCompare) * 100)}%` : '';
          mediaSave.classList.toggle('is-hidden', !hasSavings);
        }

        if (stock) {
          stock.textContent = isAvailable ? stock.dataset.stockAvailable : stock.dataset.stockUnavailable;
          stock.classList.toggle('is-unavailable', !isAvailable);
        }

        if (sku) {
          const variantSku = option.dataset.sku || '';
          sku.textContent = variantSku ? `SKU: ${variantSku}` : '';
          sku.classList.toggle('is-hidden', !variantSku);
        }

        if (submit) {
          submit.disabled = !isAvailable;
        }

        if (submitLabel && submit) {
          submitLabel.textContent = isAvailable
            ? submit.dataset.labelAvailable || 'In den Warenkorb'
            : submit.dataset.labelUnavailable || 'Aktuell nicht verfügbar';
        }

        if (stickyPrice) {
          stickyPrice.textContent = option.dataset.price || '';
        }

        if (stickyButton) {
          stickyButton.disabled = !isAvailable;
        }

        setQuantityLocked(!isAvailable);
        clampQuantity();

        if (option.dataset.mediaId) {
          setActiveMedia(option.dataset.mediaId);
        }
      };

      mediaTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => setActiveMedia(trigger.dataset.productMediaTrigger));
      });

      if (thumbsTrack && thumbsPrev && thumbsNext) {
        const scrollThumbs = (direction) => {
          const step = getThumbStep() || thumbsTrack.clientWidth;
          thumbsTrack.scrollBy({ left: direction * step, behavior: getScrollBehavior() });
        };

        thumbsPrev.addEventListener('click', () => scrollThumbs(-1));
        thumbsNext.addEventListener('click', () => scrollThumbs(1));
        thumbsTrack.addEventListener('scroll', updateThumbNavigation, { passive: true });
        window.addEventListener('resize', updateThumbNavigation);
        window.requestAnimationFrame(updateThumbNavigation);
      }

      if (variantSelect) {
        variantSelect.addEventListener('change', () => {
          updatePricing(variantSelect.selectedOptions[0]);
        });

        updatePricing(variantSelect.selectedOptions[0]);
      }

      quantityButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (!quantityInput) {
            return;
          }

          const nextValue = Math.max(1, Number(quantityInput.value || 1) + Number(button.dataset.qtyChange || 0));
          quantityInput.value = String(nextValue);
          clampQuantity();
        });
      });

      if (quantityInput) {
        quantityInput.addEventListener('change', clampQuantity);
        quantityInput.addEventListener('blur', clampQuantity);
        clampQuantity();

        if (submit && submit.disabled) {
          setQuantityLocked(true);
        }
      }

      if (zoomButton && lightbox && lightboxMedia && typeof lightbox.showModal === 'function') {
        zoomButton.addEventListener('click', () => {
          const activeSlide =
            productRoot.querySelector('[data-product-media-slide].is-active') ||
            productRoot.querySelector('[data-product-media-slide]');
          const activeMedia = activeSlide?.querySelector('img, video, iframe, model-viewer');

          if (!activeMedia) {
            return;
          }

          const zoomedMedia = activeMedia.cloneNode(true);

          if (zoomedMedia.tagName === 'IMG') {
            zoomedMedia.loading = 'eager';
            zoomedMedia.removeAttribute('sizes');
          } else if (zoomedMedia.tagName === 'VIDEO') {
            zoomedMedia.controls = true;
            zoomedMedia.autoplay = false;
            zoomedMedia.removeAttribute('autoplay');
            zoomedMedia.pause();
          }

          lightboxMedia.replaceChildren(zoomedMedia);
          lightbox.showModal();
        });

        lightboxClose?.addEventListener('click', () => lightbox.close());

        lightbox.addEventListener('click', (event) => {
          if (event.target === lightbox) {
            lightbox.close();
          }
        });

        lightbox.addEventListener('close', () => {
          lightboxMedia.replaceChildren();
          zoomButton.focus();
        });
      }

      if (stickyBar && stickyButton && submit && 'IntersectionObserver' in window) {
        stickyBar.hidden = false;

        const stickyObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
            });
          },
          { rootMargin: '0px 0px -10% 0px' }
        );

        stickyObserver.observe(submit);

        stickyButton.addEventListener('click', () => {
          if (!submit.disabled) {
            submit.click();
          }
        });
      }

      if (benefitsCard && benefitsDismiss) {
        benefitsDismiss.addEventListener('click', () => {
          benefitsCard.hidden = true;
        });
      }
    });
  };

  const bindFocusSelects = (scope = document) => {
    getScopedElements(scope, selectors.focusSelect).forEach((root) => {
      if (root.dataset.focusSelectReady === 'true') {
        return;
      }

      const select = document.getElementById(root.dataset.focusSelect);
      const button = root.querySelector('[data-focus-select-button]');
      const valueNode = root.querySelector('[data-focus-select-value]');
      const list = root.querySelector('[data-focus-select-list]');

      if (!select || !button || !valueNode || !list) {
        return;
      }

      const options = Array.from(select.options);

      if (options.length === 0) {
        return;
      }

      root.dataset.focusSelectReady = 'true';
      root.hidden = false;
      select.classList.add('is-focus-select-native');
      select.setAttribute('aria-hidden', 'true');
      select.tabIndex = -1;

      let activeIndex = Math.max(0, select.selectedIndex);
      let typeahead = '';
      let typeaheadTimer = 0;
      let pressedOptionIndex = null;

      const optionNodes = options.map((option, index) => {
        const item = document.createElement('li');
        item.id = `${list.id}-option-${index}`;
        item.className = 'focus-select-control__option';
        item.setAttribute('role', 'option');
        item.dataset.value = option.value;
        item.textContent = option.textContent.trim();

        if (option.disabled) {
          item.setAttribute('aria-disabled', 'true');
        }

        list.appendChild(item);
        return item;
      });

      const getSelectedIndex = () => Math.max(0, options.findIndex((option) => option.value === select.value));
      const listboxPopover = createListboxPopoverController(button, list);

      const setActive = (index, shouldScroll = false) => {
        activeIndex = (index + optionNodes.length) % optionNodes.length;

        optionNodes.forEach((node, nodeIndex) => {
          node.classList.toggle('is-active', nodeIndex === activeIndex);
        });

        button.setAttribute('aria-activedescendant', optionNodes[activeIndex].id);

        if (shouldScroll) {
          optionNodes[activeIndex].scrollIntoView({ block: 'nearest' });
        }
      };

      const syncSelection = () => {
        const selectedIndex = getSelectedIndex();
        const selectedOption = options[selectedIndex];

        valueNode.textContent = selectedOption.textContent.trim();

        optionNodes.forEach((node, nodeIndex) => {
          node.setAttribute('aria-selected', String(nodeIndex === selectedIndex));
        });

        setActive(selectedIndex);
      };

      const openList = () => {
        root.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        listboxPopover.open();
        setActive(getSelectedIndex(), true);
        listboxPopover.position();
      };

      const closeList = (focusButton = true) => {
        root.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
        listboxPopover.close();
        pressedOptionIndex = null;

        if (focusButton) {
          button.focus({ preventScroll: true });
        }
      };

      const chooseOption = (index) => {
        const option = options[index];

        if (!option || option.disabled) {
          return;
        }

        if (select.value !== option.value) {
          select.value = option.value;
          syncSelection();
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }

        closeList();
      };

      const matchTypeahead = (key) => {
        typeahead += key.toLocaleLowerCase('de-DE');
        window.clearTimeout(typeaheadTimer);
        typeaheadTimer = window.setTimeout(() => {
          typeahead = '';
        }, 700);

        const startIndex = activeIndex + 1;
        const orderedOptions = options.slice(startIndex).concat(options.slice(0, startIndex));
        const match = orderedOptions.find((option) => option.textContent.trim().toLocaleLowerCase('de-DE').startsWith(typeahead));

        if (match) {
          setActive(options.indexOf(match), true);
        }
      };

      const isPrintableKey = (event) => event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey;

      const handlePrintableKey = (event) => {
        if (!isPrintableKey(event)) {
          return false;
        }

        event.preventDefault();
        if (!root.classList.contains('is-open')) {
          openList();
        }
        matchTypeahead(event.key);
        return true;
      };

      const handleNavigationKey = (event) => {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            if (!root.classList.contains('is-open')) {
              openList();
            }
            setActive(activeIndex + 1, true);
            return true;
          case 'ArrowUp':
            event.preventDefault();
            if (!root.classList.contains('is-open')) {
              openList();
            }
            setActive(activeIndex - 1, true);
            return true;
          case 'Home':
            event.preventDefault();
            if (!root.classList.contains('is-open')) {
              openList();
            }
            setActive(0, true);
            return true;
          case 'End':
            event.preventDefault();
            if (!root.classList.contains('is-open')) {
              openList();
            }
            setActive(optionNodes.length - 1, true);
            return true;
          default:
            return false;
        }
      };

      button.addEventListener('click', () => {
        if (root.classList.contains('is-open')) {
          closeList();
        } else {
          openList();
        }
      });

      button.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (root.classList.contains('is-open')) {
            chooseOption(activeIndex);
          } else {
            openList();
          }
        } else if (event.key === 'Escape' && root.classList.contains('is-open')) {
          event.preventDefault();
          closeList();
        } else if (event.key === 'Tab') {
          closeList(false);
        } else if (handleNavigationKey(event) || handlePrintableKey(event)) {
          return;
        }
      });

      optionNodes.forEach((node, index) => {
        node.addEventListener('pointerenter', () => setActive(index));
        node.addEventListener('pointerdown', (event) => {
          pressedOptionIndex = index;
          setActive(index);

          if (event.pointerType !== 'touch') {
            event.preventDefault();
          }
        });
        node.addEventListener('pointerup', () => {
          if (pressedOptionIndex === index) {
            chooseOption(index);
          }
        });
        node.addEventListener('pointercancel', () => {
          pressedOptionIndex = null;
        });
        node.addEventListener('mousedown', (event) => {
          event.preventDefault();
        });
        node.addEventListener('click', () => chooseOption(index));
      });

      document.addEventListener('pointerdown', (event) => {
        if (root.classList.contains('is-open') && !root.contains(event.target)) {
          closeList(false);
        }
      });

      root.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (root.classList.contains('is-open') && pressedOptionIndex === null && !root.contains(document.activeElement)) {
            closeList(false);
          }
        });
      });

      select.addEventListener('change', syncSelection);
      syncSelection();
    });
  };

  let cartDrawerBound = false;

  const bindCartDrawer = () => {
    if (cartDrawerBound) {
      return;
    }

    const drawerRoot = document.querySelector('[data-cart-drawer]');

    if (!drawerRoot) {
      return;
    }

    cartDrawerBound = true;

    let lastFocus = null;
    let closeTimer = 0;

    const announcer = document.createElement('p');
    announcer.className = 'visually-hidden';
    announcer.setAttribute('role', 'status');
    document.body.appendChild(announcer);

    const announce = (message) => {
      announcer.textContent = '';
      window.requestAnimationFrame(() => {
        announcer.textContent = message;
      });
    };

    const formatMoney = (cents, template = '0,00 €') => {
      const value = Math.max(Number(cents) || 0, 0);
      const numberMatch = template.match(/\d[\d.,\s\u00a0]*\d|\d/);
      const decimalSeparator = (template.match(/\d([,.])\d{2}/) || [])[1] || ',';
      const groupSeparator = decimalSeparator === ',' ? '.' : ',';
      const [integer, fraction] = (value / 100).toFixed(2).split('.');
      const amount = `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)}${decimalSeparator}${fraction}`;

      return numberMatch ? template.replace(numberMatch[0], amount) : amount;
    };

    const updateFreeShippingProgress = (cart = null) => {
      document.querySelectorAll('[data-free-shipping-progress]').forEach((progress) => {
        const threshold = Math.max(Number(progress.dataset.threshold || 4900), 1);
        const total = Number(cart?.total_price ?? progress.dataset.total ?? 0);
        const remaining = threshold - total;
        const value = Math.min(Math.max(total, 0), threshold);
        const fill = progress.querySelector('[data-free-shipping-fill]');
        const bar = progress.querySelector('[data-free-shipping-bar]');
        const message = progress.querySelector('[data-free-shipping-message]');

        progress.hidden = cart && Number(cart.item_count || 0) === 0;
        progress.dataset.total = String(total);

        if (fill) {
          fill.style.width = `${Math.min((value / threshold) * 100, 100)}%`;
        }

        if (bar) {
          bar.setAttribute('aria-valuemax', String(threshold));
          bar.setAttribute('aria-valuenow', String(value));
        }

        if (message) {
          message.textContent =
            remaining > 0
              ? `Nur noch ${formatMoney(remaining, progress.dataset.moneyTemplate)} bis zum kostenlosen Versand`
              : '🎉 Kostenloser Versand freigeschaltet';
        }
      });
    };

    const getFocusable = () =>
      Array.from(
        drawerRoot.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.offsetParent !== null);

    const getPanel = () => drawerRoot.querySelector('[data-cart-drawer-panel]');

    const isReducedMotion = () => document.documentElement.dataset.motion === 'reduced';

    const getCartPageRoot = () => document.querySelector('[data-cart-page-section]');

    const getCartPageSectionId = () => getCartPageRoot()?.dataset.cartPageSection || '';

    const getCartSections = () => ['cart-drawer', getCartPageSectionId()].filter(Boolean).join(',');

    const updateHeaderCount = () => {
      const count = Number(drawerRoot.dataset.cartCount || 0);

      document.querySelectorAll('.site-header__cart').forEach((cartLink) => {
        let badge = cartLink.querySelector('.site-header__cart-count');

        if (count > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'site-header__cart-count';
            cartLink.prepend(badge);
          }

          badge.textContent = String(count);
        } else if (badge) {
          badge.remove();
        }
      });
    };

    const applyDrawerHtml = (html) => {
      if (!html) {
        return false;
      }

      const template = document.createElement('div');
      template.innerHTML = html;
      const fresh = template.querySelector('[data-cart-drawer]');

      if (!fresh) {
        return false;
      }

      drawerRoot.innerHTML = fresh.innerHTML;
      drawerRoot.dataset.cartCount = fresh.dataset.cartCount || '0';
      updateHeaderCount();
      updateFreeShippingProgress();
      return true;
    };

    const applyCartPageHtml = (html) => {
      const cartPageRoot = getCartPageRoot();

      if (!cartPageRoot || !html) {
        return false;
      }

      const template = document.createElement('div');
      template.innerHTML = html;
      const fresh = template.querySelector('[data-cart-page-section]');

      if (!fresh) {
        return false;
      }

      cartPageRoot.replaceWith(fresh);
      bindFocusReveals(fresh);
      updateFreeShippingProgress();
      return true;
    };

    const applyCartPayload = (payload) => {
      const sections = payload?.sections || {};
      const cartPageSectionId = getCartPageSectionId();
      const drawerApplied = applyDrawerHtml(sections['cart-drawer']);
      const pageApplied = applyCartPageHtml(cartPageSectionId ? sections[cartPageSectionId] : '');

      if (!drawerApplied && Number.isFinite(Number(payload?.item_count))) {
        drawerRoot.dataset.cartCount = String(payload.item_count);
        updateHeaderCount();
      }

      if (Number.isFinite(Number(payload?.total_price))) {
        updateFreeShippingProgress(payload);
      }

      return drawerApplied || pageApplied;
    };

    const refresh = async () => {
      try {
        const response = await fetch(`${window.location.pathname}?sections=cart-drawer`, {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          return;
        }

        const sections = await response.json();
        applyDrawerHtml(sections['cart-drawer']);
      } catch (error) {
        // Network hiccup: keep the previous drawer state.
      }
    };

    const openDrawer = () => {
      window.clearTimeout(closeTimer);
      lastFocus = document.activeElement;
      drawerRoot.hidden = false;

      window.requestAnimationFrame(() => {
        drawerRoot.classList.add('is-open');
        getPanel()?.focus();
      });
    };

    const closeDrawer = () => {
      drawerRoot.classList.remove('is-open');

      const finalize = () => {
        drawerRoot.hidden = true;

        if (lastFocus instanceof HTMLElement) {
          lastFocus.focus();
        }
      };

      if (isReducedMotion()) {
        finalize();
      } else {
        closeTimer = window.setTimeout(finalize, 280);
      }
    };

    const changeLine = async (line, quantity) => {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(line),
            quantity: Math.max(Number(quantity) || 0, 0),
            sections: getCartSections(),
            sections_url: window.location.pathname,
          }),
        });

        const payload = await response.json().catch(() => ({}));

        if (applyCartPayload(payload)) {
          return;
        }
      } catch (error) {
        // Fall through to a full refresh below.
      }

      await refresh();
    };

    drawerRoot.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest('[data-cart-drawer-close]');

      if (closeTrigger) {
        closeDrawer();
        return;
      }

      const qtyTrigger = event.target.closest('[data-cart-qty-change]');

      if (qtyTrigger) {
        const input = qtyTrigger
          .closest('.focus-cart-drawer__qty')
          ?.querySelector('[data-cart-qty-input]');
        const current = Number(input?.value || 1);
        changeLine(qtyTrigger.dataset.line, current + Number(qtyTrigger.dataset.qtyChange || qtyTrigger.dataset.cartQtyChange || 0));
        return;
      }

      const removeTrigger = event.target.closest('[data-cart-remove]');

      if (removeTrigger) {
        changeLine(removeTrigger.dataset.line, 0);
      }
    });

    document.addEventListener('click', (event) => {
      const removeTrigger = event.target.closest('[data-cart-page-remove]');

      if (!removeTrigger) {
        return;
      }

      event.preventDefault();
      changeLine(removeTrigger.dataset.line, 0);
    });

    drawerRoot.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-qty-input]');

      if (input) {
        changeLine(input.dataset.line, input.value);
      }
    });

    document.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-page-qty]');

      if (input) {
        changeLine(input.dataset.line, input.value);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (drawerRoot.hidden) {
        return;
      }

      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }

      if (event.key === 'Tab') {
        const focusable = getFocusable();

        if (focusable.length === 0) {
          event.preventDefault();
          getPanel()?.focus();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (event.shiftKey && (active === first || !drawerRoot.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !drawerRoot.contains(active))) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    const showFormFeedback = (form, message) => {
      let feedback = form.parentElement.querySelector('[data-cart-feedback]');

      if (!feedback) {
        feedback = document.createElement('p');
        feedback.className = 'focus-cart-feedback';
        feedback.setAttribute('data-cart-feedback', '');
        feedback.setAttribute('role', 'alert');
        form.insertAdjacentElement('afterend', feedback);
      }

      feedback.textContent = message;
      feedback.hidden = !message;
    };

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('form[action*="/cart/add"]');

      if (!form) {
        return;
      }

      event.preventDefault();

      const submitButton = form.querySelector('[type="submit"]');
      submitButton?.setAttribute('aria-busy', 'true');

      const formData = new FormData(form);
      formData.append('sections', getCartSections());
      formData.append('sections_url', window.location.pathname);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      })
        .then(async (response) => {
          const payload = await response.json().catch(() => ({}));

          if (!response.ok) {
            showFormFeedback(form, payload.description || payload.message || 'Konnte nicht zum Warenkorb hinzugefügt werden.');
            return;
          }

          showFormFeedback(form, '');

          if (!applyCartPayload(payload)) {
            await refresh();
          }

          announce('Artikel wurde zum Warenkorb hinzugefügt.');
          openDrawer();
        })
        .catch(() => {
          showFormFeedback(form, 'Verbindung fehlgeschlagen. Bitte versuche es erneut.');
        })
        .finally(() => {
          submitButton?.removeAttribute('aria-busy');
        });
    });

    document.querySelectorAll('a.site-header__cart').forEach((cartLink) => {
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        openDrawer();
        refresh();
      });
    });

    updateHeaderCount();
    updateFreeShippingProgress();
  };

  const parseProvinces = (option) => {
    try {
      return JSON.parse(option?.dataset.provinces || '[]');
    } catch (error) {
      return [];
    }
  };

  const bindAddressForms = (scope = document) => {
    getScopedElements(scope, selectors.addressForm).forEach((form) => {
      if (form.dataset.addressReady === 'true') {
        return;
      }

      form.dataset.addressReady = 'true';

      const countrySelect = form.querySelector('[data-address-country]');
      const provinceContainer = form.querySelector('[data-address-province-container]');
      const provinceSelect = form.querySelector('[data-address-province]');

      if (!countrySelect || !provinceContainer || !provinceSelect) {
        return;
      }

      const defaultCountry = countrySelect.dataset.default;

      if (defaultCountry) {
        countrySelect.value = defaultCountry;
      }

      const updateProvinces = () => {
        const selectedOption = countrySelect.options[countrySelect.selectedIndex];
        const provinces = parseProvinces(selectedOption);
        const defaultProvince = provinceSelect.dataset.default;

        provinceSelect.innerHTML = '';

        if (provinces.length === 0) {
          provinceContainer.hidden = true;
          return;
        }

        provinceContainer.hidden = false;

        provinces.forEach((province) => {
          const option = document.createElement('option');
          option.value = province[0];
          option.textContent = province[1];

          if (defaultProvince && (defaultProvince === province[0] || defaultProvince === province[1])) {
            option.selected = true;
          }

          provinceSelect.appendChild(option);
        });
      };

      countrySelect.addEventListener('change', updateProvinces);
      updateProvinces();
    });

    getScopedElements(scope, selectors.addressDelete).forEach((button) => {
      if (button.dataset.addressDeleteReady === 'true') {
        return;
      }

      button.dataset.addressDeleteReady = 'true';

      button.addEventListener('click', () => {
        const message = button.dataset.confirmMessage || 'Adresse löschen?';

        if (!window.confirm(message)) {
          return;
        }

        const form = document.createElement('form');
        form.method = 'post';
        form.action = button.dataset.addressTarget;

        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        methodInput.value = 'delete';

        form.appendChild(methodInput);
        document.body.appendChild(form);
        form.submit();
      });
    });
  };

  const bindCollectionSearch = (scope = document) => {
    getScopedElements(scope, selectors.collectionSearch).forEach((searchRoot) => {
      if (searchRoot.dataset.collectionSearchReady === 'true') {
        return;
      }

      const form = searchRoot.querySelector('[data-collection-search-form]');
      const input = searchRoot.querySelector('[data-collection-search-input]');
      const clearButton = searchRoot.querySelector('[data-collection-search-clear]');
      const status = searchRoot.querySelector('[data-collection-search-status]');
      const grid = searchRoot.querySelector('[data-collection-grid]');
      const pagination = searchRoot.querySelector('[data-collection-search-pagination]');
      const emptyState = searchRoot.querySelector('[data-collection-search-empty]');
      const emptyTerm = searchRoot.querySelector('[data-collection-search-term]');
      const emptyReset = searchRoot.querySelector('[data-collection-search-empty-reset]');
      const filterToggle = searchRoot.querySelector('[data-collection-filter-toggle]');
      const filterToggleLabel = searchRoot.querySelector('[data-collection-filter-toggle-label]');
      const filtersPanel = searchRoot.querySelector('[data-collection-mobile-filters]');
      const cards = Array.from(searchRoot.querySelectorAll('[data-collection-card]'));

      if (!form || !input || !grid || cards.length === 0) {
        return;
      }

      searchRoot.dataset.collectionSearchReady = 'true';

      const syncMobileFilters = () => {
        if (!filterToggle || !filtersPanel || !mobileCollectionFiltersQuery) {
          return;
        }

        const isMobile = mobileCollectionFiltersQuery.matches;
        const isOpen = searchRoot.classList.contains('is-mobile-filters-open');

        if (!isMobile) {
          searchRoot.classList.remove('is-mobile-filters-open');
          filtersPanel.hidden = false;
          filterToggle.setAttribute('aria-expanded', 'false');

          if (filterToggleLabel) {
            filterToggleLabel.textContent = 'Filter öffnen';
          }

          return;
        }

        filtersPanel.hidden = !isOpen;
        filterToggle.setAttribute('aria-expanded', String(isOpen));

        if (filterToggleLabel) {
          filterToggleLabel.textContent = isOpen ? 'Filter schließen' : 'Filter öffnen';
        }
      };

      const cardIndex = cards.map((card) => ({
        card,
        searchText: normalizeSearchValue(card.dataset.collectionSearchText || card.textContent),
      }));

      const setSearchState = () => {
        const query = normalizeSearchValue(input.value);
        const hasQuery = query.length > 0;
        let matches = 0;

        cardIndex.forEach(({ card, searchText }) => {
          const isVisible = !hasQuery || searchText.includes(query);
          card.hidden = !isVisible;

          if (isVisible) {
            matches += 1;
          }
        });

        if (clearButton) {
          clearButton.hidden = !hasQuery;
        }

        if (!hasQuery) {
          if (status) {
            status.hidden = true;
            status.textContent = '';
          }

          if (emptyState) {
            emptyState.hidden = true;
          }

          grid.hidden = false;

          if (pagination) {
            pagination.hidden = false;
          }

          return;
        }

        if (status) {
          status.hidden = false;
          status.textContent =
            matches === 1
              ? '1 Produkt passt zu deiner Suche in dieser Ansicht.'
              : `${matches} Produkte passen zu deiner Suche in dieser Ansicht.`;
        }

        const hasMatches = matches > 0;
        grid.hidden = !hasMatches;

        if (pagination) {
          pagination.hidden = true;
        }

        if (emptyState) {
          emptyState.hidden = hasMatches;
        }

        if (emptyTerm) {
          emptyTerm.textContent = input.value.trim();
        }
      };

      const updateSearchState = debounce(setSearchState, 120);

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        setSearchState();
      });

      input.addEventListener('input', updateSearchState);

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && input.value) {
          input.value = '';
          setSearchState();
        }
      });

      if (clearButton) {
        clearButton.addEventListener('click', () => {
          input.value = '';
          input.focus();
          setSearchState();
        });
      }

      if (emptyReset) {
        emptyReset.addEventListener('click', () => {
          input.value = '';
          input.focus();
          setSearchState();
        });
      }

      if (filterToggle && filtersPanel) {
        searchRoot.classList.remove('is-mobile-filters-open');

        filterToggle.addEventListener('click', () => {
          if (mobileCollectionFiltersQuery && !mobileCollectionFiltersQuery.matches) {
            return;
          }

          searchRoot.classList.toggle('is-mobile-filters-open');
          syncMobileFilters();
        });

        syncMobileFilters();

        const handleFilterViewportChange = () => syncMobileFilters();

        if (mobileCollectionFiltersQuery) {
          if (typeof mobileCollectionFiltersQuery.addEventListener === 'function') {
            mobileCollectionFiltersQuery.addEventListener('change', handleFilterViewportChange);
          } else if (typeof mobileCollectionFiltersQuery.addListener === 'function') {
            mobileCollectionFiltersQuery.addListener(handleFilterViewportChange);
          }
        }
      }
    });
  };

  const humanizePolicyHandle = (handle) =>
    String(handle || '')
      .split('-')
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');

  const getPolicyPageCopy = (pathname) => {
    const handle = String(pathname || '')
      .replace(/\/+$/, '')
      .split('/')
      .pop();

    const defaultCopy = {
      eyebrow: 'Rechtliches',
      lead: 'Alle wichtigen Hinweise zu Datenschutz, Versand, Widerruf, AGB und weiteren Shop-Richtlinien in einer klaren, gut lesbaren Focus-Darstellung.',
      title: '',
      navLabel: '',
    };

    const copyByHandle = {
      'privacy-policy': {
        eyebrow: 'Datenschutz & DSGVO',
        lead: 'Hier finden Sie Informationen dazu, welche personenbezogenen Daten wir verarbeiten, auf welcher Grundlage dies geschieht und welche Rechte Ihnen dabei zustehen.',
      },
      'refund-policy': {
        eyebrow: 'Widerruf & Retouren',
        lead: 'Diese Seite bündelt Hinweise zu Widerruf, Rücksendung, Fristen und Erstattungen in einer strukturierten, leicht verständlichen Form.',
      },
      'shipping-policy': {
        eyebrow: 'Versand & Lieferung',
        lead: 'Hier finden Sie Informationen zu Versandarten, Liefergebieten, Lieferzeiten und anfallenden Versandkosten.',
      },
      'terms-of-service': {
        eyebrow: 'AGB & Bestellung',
        lead: 'Diese Bedingungen regeln Vertragsschluss, Preise, Lieferung, Gewährleistung und weitere Grundlagen für Bestellungen in unserem Shop.',
      },
      'legal-notice': {
        eyebrow: 'Impressum',
        lead: 'Hier finden Sie die gesetzlich vorgeschriebenen Anbieterinformationen sowie Kontakt- und Verantwortlichkeitsangaben.',
      },
      'contact-information': {
        eyebrow: 'Kontakt',
        lead: 'Auf dieser Seite finden Sie unsere Kontakt- und Erreichbarkeitsangaben für Anfragen zu Bestellungen, Versand, Retouren und allgemeinen Anliegen.',
      },
      'subscription-policy': {
        eyebrow: 'Abonnements & Kündigung',
        title: 'Subscription policy',
        navLabel: 'Subscription Policy',
        lead: 'Hier finden Sie Hinweise zu Abonnements, Vorabbestellungen, Testkäufen sowie den Regeln zur Stornierung dieser besonderen Bestellarten.',
      },
    };

    return {
      ...defaultCopy,
      ...(copyByHandle[handle] || {}),
    };
  };

  const getPolicyLinks = () => {
    const embeddedPolicyData = document.querySelector(selectors.policyData);

    if (embeddedPolicyData) {
      try {
        const embeddedPolicies = JSON.parse(embeddedPolicyData.textContent || '[]');
        const seenPaths = new Set();
        const links = embeddedPolicies.reduce((policies, policyEntry) => {
          const path = String(policyEntry?.url || '').replace(/\/+$/, '');

          if (!path.startsWith('/policies/') || seenPaths.has(path)) {
            return policies;
          }

          seenPaths.add(path);
          policies.push({
            href: path,
            label: String(policyEntry?.title || humanizePolicyHandle(path.split('/').pop())).trim(),
            path,
          });

          return policies;
        }, []);

        if (links.length) {
          return links;
        }
      } catch (error) {
        console.warn('Unable to parse embedded policy navigation data.', error);
      }
    }

    const sourceRoot = document.querySelector('[role="contentinfo"], footer');
    const sourceLinks = sourceRoot ? Array.from(sourceRoot.querySelectorAll('a[href*="/policies/"]')) : [];
    const seenPaths = new Set();

    return sourceLinks.reduce((links, link) => {
      try {
        const url = new URL(link.getAttribute('href') || '', window.location.origin);
        const path = url.pathname.replace(/\/+$/, '');

        if (!path.startsWith('/policies/') || seenPaths.has(path)) {
          return links;
        }

        seenPaths.add(path);
        links.push({
          href: `${path}${url.search}`,
          label: link.textContent.trim() || humanizePolicyHandle(path.split('/').pop()),
          path,
        });
      } catch (error) {
        console.warn('Unable to read footer policy link.', error);
        return links;
      }

      return links;
    }, []);
  };

  const bindPolicyPage = (scope = document) => {
    if (!document.body.classList.contains('template-policy')) {
      return;
    }

    const container =
      scope instanceof Element && scope.matches(selectors.policyContainer)
        ? scope
        : document.querySelector(selectors.policyContainer);

    if (!container || container.dataset.focusPolicyReady === 'true') {
      return;
    }

    const titleRoot = container.querySelector('.shopify-policy__title');
    const titleHeading = titleRoot ? titleRoot.querySelector('h1') : null;
    const bodyRoot = container.querySelector('.shopify-policy__body');

    if (!titleRoot || !titleHeading || !bodyRoot) {
      return;
    }

    container.dataset.focusPolicyReady = 'true';

    const currentPath = window.location.pathname.replace(/\/+$/, '');
    const copy = getPolicyPageCopy(currentPath);
    const policyLinks = getPolicyLinks();

    container.classList.add('focus-page-shell', 'focus-policy-page');
      titleRoot.classList.add('focus-page__header', 'focus-policy-page__header');
      titleRoot.dataset.focusReveal = '';
      titleHeading.classList.add('focus-page__title');

      if (copy.title) {
        titleHeading.textContent = copy.title;
      }

      if (!titleRoot.querySelector('.focus-page__eyebrow')) {
        const eyebrow = document.createElement('p');
        eyebrow.className = 'focus-page__eyebrow';
        eyebrow.textContent = copy.eyebrow;
      titleRoot.insertBefore(eyebrow, titleHeading);
    }

    if (!titleRoot.querySelector('.focus-page__lead')) {
      const lead = document.createElement('p');
      lead.className = 'focus-page__lead';
      lead.textContent = copy.lead;
      titleRoot.appendChild(lead);
    }

      if (policyLinks.length > 1 && !container.querySelector('[data-focus-policy-nav]')) {
        const nav = document.createElement('nav');
        const navHeader = document.createElement('div');
        const navTitle = document.createElement('h2');
        const navCopy = document.createElement('p');
      const navLinks = document.createElement('div');

      nav.className = 'focus-pane focus-pane--soft focus-policy-nav';
      nav.dataset.focusPolicyNav = 'true';
      nav.dataset.focusReveal = '';
      nav.style.setProperty('--focus-reveal-delay', '90ms');
      nav.setAttribute('aria-label', 'Weitere Richtlinien');

      navHeader.className = 'focus-policy-nav__header';
      navTitle.className = 'focus-policy-nav__title';
      navCopy.className = 'focus-policy-nav__copy';
      navLinks.className = 'focus-policy-nav__links';

      navTitle.textContent = 'Weitere Richtlinien';
      navCopy.textContent =
        'Wechseln Sie direkt zwischen Datenschutz, Versand, Widerruf, AGB und weiteren rechtlichen Informationen.';

      policyLinks.forEach((policyLink) => {
        const link = document.createElement('a');
        const isActive = policyLink.path === currentPath;
        const policyLinkCopy = getPolicyPageCopy(policyLink.path);

        link.className = `focus-button focus-button--secondary focus-policy-nav__link${isActive ? ' is-active' : ''}`;
        link.href = policyLink.href;
        link.textContent = policyLinkCopy.navLabel || policyLink.label;

        if (isActive) {
          link.setAttribute('aria-current', 'page');
        }

        navLinks.appendChild(link);
      });

      navHeader.append(navTitle, navCopy);
      nav.append(navHeader, navLinks);
      bodyRoot.insertAdjacentElement('afterend', nav);
    }

    bodyRoot.classList.add('focus-pane', 'focus-richtext', 'focus-policy-body');

    const richText = bodyRoot.querySelector('.rte');

    if (richText) {
      richText.classList.add('focus-policy-body__rte');
    }
  };

  const initialize = (scope = document) => {
    applyMotionMode();
    bindPolicyPage(scope);
    bindFocusReveals(scope);
    bindFocusSelects(scope);
    bindFocusProducts(scope);
    bindCartDrawer();
    bindAddressForms(scope);
    bindCollectionSearch(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  if (getMotionPreference() === 'system' && reducedMotionQuery) {
    const syncMotionMode = () => applyMotionMode();

    if (typeof reducedMotionQuery.addEventListener === 'function') {
      reducedMotionQuery.addEventListener('change', syncMotionMode);
    } else if (typeof reducedMotionQuery.addListener === 'function') {
      reducedMotionQuery.addListener(syncMotionMode);
    }
  }
})();
